import { Injectable } from '@angular/core';
import { RouteProvider } from '../route/route';
import { LoadingProvider } from '../../providers/loading/loading';
import { LoggerProvider } from '../logger/logger';
import { COUNTRY_DECLARATION_HEADER, COUNTRY_DECLARATION_REASON_CODE } from '../myria/app-variables';

export interface ICountryDeclarationError {
  status: number;
  reasonCode: string;
  message?: string;
  countrySelectionId?: string;
}

@Injectable()
export class CountryDeclarationProvider {

  private countrySelectionId: string = null;
  private declaredCountryCode: string = null;
  private selectionExpired = false;

  constructor(private routeProvider: RouteProvider,
    private loadingProvider: LoadingProvider) { }

  parseError(errorObject: any): ICountryDeclarationError {
    if (!errorObject)
      return null;

    let err = errorObject.error;

    if (err !== null && typeof err === 'string') {
      try {
        err = JSON.parse(err);
      } catch (e) {
        return null;
      }
    }

    if (!err || err.Allowed !== false || err.ReasonCode === undefined || err.ReasonCode === null)
      return null;

    return {
      status: errorObject.status,
      reasonCode: String(err.ReasonCode),
      message: typeof err.Message === 'string' ? err.Message : undefined,
      countrySelectionId: typeof err.CountrySelectionId === 'string' ? err.CountrySelectionId : undefined
    };
  }

  getHeaders() {
    if (!this.countrySelectionId || !this.declaredCountryCode)
      return {};

    return {
      [COUNTRY_DECLARATION_HEADER.SELECTION_ID]: this.countrySelectionId,
      [COUNTRY_DECLARATION_HEADER.DECLARED_COUNTRY_CODE]: this.declaredCountryCode
    };
  }

  clear() {
    this.countrySelectionId = null;
    this.declaredCountryCode = null;
    this.selectionExpired = false;
  }

  isCancellation(errorObject: any): boolean {
    return !!errorObject && errorObject.countryDeclarationCancelled === true;
  }

  isRestrictionShown(errorObject: any): boolean {
    return !!errorObject && errorObject.ipRestrictionShown === true;
  }

  async execute(requestFn: () => Promise<any>, hasRestartedExpiredSelection = false): Promise<any> {
    try {
      const result = await requestFn();
      this.clear();
      return result;
    } catch (ex) {
      const declarationError = this.parseError(ex);

      if (this.isCountryNotIdentified(declarationError)) {
        LoggerProvider.info(this, 'Country not identified by IP - asking the user to declare their country');
        this.countrySelectionId = declarationError.countrySelectionId;
        this.declaredCountryCode = null;

        const countryCode = await this.promptForCountry();
        if (!countryCode) {
          this.clear();
          throw { countryDeclarationCancelled: true };
        }

        this.declaredCountryCode = countryCode;
        return this.execute(requestFn);
      }

      if (this.isSelectionExpired(declarationError) && !hasRestartedExpiredSelection) {
        LoggerProvider.info(this, 'Country selection id expired - restarting the declaration flow');
        this.clear();
        this.selectionExpired = true;
        return this.execute(requestFn, true);
      }

      this.clear();
      throw ex;
    }
  }

  private isCountryNotIdentified(declarationError: ICountryDeclarationError): boolean {
    return !!declarationError &&
      declarationError.reasonCode === COUNTRY_DECLARATION_REASON_CODE.COUNTRY_NOT_IDENTIFIED &&
      !!declarationError.countrySelectionId;
  }

  private isSelectionExpired(declarationError: ICountryDeclarationError): boolean {
    return !!declarationError &&
      declarationError.reasonCode === COUNTRY_DECLARATION_REASON_CODE.SELECTION_ID_INVALID_OR_EXPIRED;
  }

  private async promptForCountry(): Promise<string> {
    const hadLoadingOverlay = this.loadingProvider.isLoading();
    if (hadLoadingOverlay)
      await this.loadingProvider.hide();

    const showExpiredMessage = this.selectionExpired;
    this.selectionExpired = false;

    const countryCode = await new Promise<string>((resolve) => {
      this.routeProvider.openModal(this.routeProvider.pages.CountryDeclarationPage, { showExpiredMessage }, (data) => {
        resolve(data && data.countryCode ? data.countryCode : null);
      });
    });

    if (hadLoadingOverlay && countryCode)
      this.loadingProvider.show();

    return countryCode;
  }
}



// WEBPACK FOOTER //
// ./src/providers/country-declaration/country-declaration.ts